import { useEffect, useState } from "react";
import { Loader2, Percent, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const EMPTY = { scope: "product", target_id: "", target_ids: [], min_quantity: 3, discount_percent: 10, active: true };
const inputClass = "w-full rounded-xl border border-amber-900/20 bg-cream px-4 py-3 text-sm outline-none transition-colors focus:border-amber-deep";

export default function AdminPricing() {
  const [tiers, setTiers] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/pricing").then(({ data }) => setTiers(data)).catch(() => setTiers([]));
  useEffect(() => {
    load();
    api.get("/admin/products").then(({ data }) => setProducts(data)).catch(() => {});
    api.get("/admin/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  const targetName = (t) => {
    if (t.scope === "global") return "Tous les produits (au panier)";
    if (t.scope === "product") return products.find((p) => p.product_id === t.target_id)?.name || t.target_id;
    return categories.find((c) => c.category_id === t.target_id)?.name || t.target_id;
  };

  const openCreate = () => {
    setForm(EMPTY);
    setEditingId(null);
    setFormOpen(true);
  };

  const openEdit = (t) => {
    setForm({ scope: t.scope, target_id: t.scope === "product" ? t.target_id : "", target_ids: t.scope === "category" ? [t.target_id] : [], min_quantity: t.min_quantity, discount_percent: t.discount_percent, active: t.active });
    setEditingId(t.tier_id);
    setFormOpen(true);
  };

  const toggleCategory = (id) =>
    setForm((f) => ({ ...f, target_ids: f.target_ids.includes(id) ? f.target_ids.filter((x) => x !== id) : [...f.target_ids, id] }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isCategory = form.scope === "category";
    const isGlobal = form.scope === "global";
    if (!isGlobal && (isCategory ? form.target_ids.length === 0 : !form.target_id)) {
      toast.error(isCategory ? "Choisissez au moins une catégorie" : "Choisissez un produit");
      return;
    }
    setSaving(true);
    const base = { scope: form.scope, min_quantity: parseInt(form.min_quantity, 10) || 2, discount_percent: parseInt(form.discount_percent, 10) || 1, active: form.active };
    try {
      if (isGlobal) {
        if (editingId) {
          await api.put(`/admin/pricing/${editingId}`, { ...base, target_id: "all" });
          toast.success("Palier mis à jour");
        } else {
          await api.post("/admin/pricing", { ...base, target_id: "all" });
          toast.success("Palier panier créé — remise sur tous les produits");
        }
      } else if (editingId) {
        if (isCategory) {
          await api.put(`/admin/pricing/${editingId}`, { ...base, target_id: form.target_ids[0] });
          for (const catId of form.target_ids.slice(1)) {
            await api.post("/admin/pricing", { ...base, target_id: catId });
          }
          toast.success(form.target_ids.length > 1 ? `Palier mis à jour — ${form.target_ids.length} catégories` : "Palier mis à jour");
        } else {
          await api.put(`/admin/pricing/${editingId}`, { ...base, target_id: form.target_id });
          toast.success("Palier mis à jour");
        }
      } else if (isCategory) {
        for (const catId of form.target_ids) {
          await api.post("/admin/pricing", { ...base, target_id: catId });
        }
        toast.success(form.target_ids.length > 1 ? `${form.target_ids.length} paliers créés — visibles sur la boutique` : "Palier créé — visible sur la boutique");
      } else {
        await api.post("/admin/pricing", { ...base, target_id: form.target_id });
        toast.success("Palier créé — visible sur la boutique");
      }
      setFormOpen(false);
      setEditingId(null);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Supprimer ce palier (−${t.discount_percent} % dès ${t.min_quantity}) ?`)) return;
    try {
      await api.delete(`/admin/pricing/${t.tier_id}`);
      toast.success("Palier supprimé");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-pricing" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-bark">Grille tarifaire</h1>
          <p className="mt-1 text-sm text-bark-soft">Remises par volume : par produit, par catégorie ou sur l'ensemble du panier. Affichées sur les fiches et appliquées au panier.</p>
        </div>
        <button
          data-testid="new-tier-button"
          onClick={openCreate}
          className="flex items-center gap-2 rounded-full bg-amber-deep px-5 py-2.5 text-sm font-medium text-white shadow-md shadow-amber-900/15 transition-all duration-200 hover:bg-honey active:scale-95"
        >
          <Plus size={16} /> Nouveau palier
        </button>
      </div>

      {formOpen && (
        <form data-testid="tier-form" onSubmit={handleSubmit} className="space-y-4 rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-xl font-semibold text-bark">{editingId ? "Modifier le palier" : "Nouveau palier de remise"}</h2>
            <button type="button" data-testid="tier-form-close" onClick={() => setFormOpen(false)} className="text-stone-mute hover:text-bark" aria-label="Fermer">
              <X size={18} />
            </button>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="tf-scope" className="mb-1.5 block text-sm font-medium text-bark">S'applique à</label>
              <select id="tf-scope" data-testid="tf-scope" value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value, target_id: "", target_ids: [] })} className={inputClass}>
                <option value="product">Un produit précis</option>
                <option value="category">Une ou plusieurs catégories</option>
                <option value="global">Tous les produits (au panier)</option>
              </select>
            </div>
            <div>
              {form.scope === "global" ? (
                <div data-testid="tf-global-note" className="rounded-xl border border-sage/30 bg-sage/5 p-3 text-sm text-bark-soft">
                  La remise s'applique dès que le client atteint la quantité totale indiquée, <strong>quels que soient les produits choisis</strong> (hors coffrets et visites).
                </div>
              ) : form.scope === "product" ? (
                <>
                  <label htmlFor="tf-target" className="mb-1.5 block text-sm font-medium text-bark">Produit *</label>
                  <select id="tf-target" data-testid="tf-target" required value={form.target_id} onChange={(e) => setForm({ ...form, target_id: e.target.value })} className={inputClass}>
                    <option value="">— Choisir —</option>
                    {products.map((p) => <option key={p.product_id} value={p.product_id}>{p.name}</option>)}
                  </select>
                </>
              ) : (
                <>
                  <label className="mb-1.5 block text-sm font-medium text-bark">
                    Catégories * {form.target_ids.length > 0 && <span className="font-normal text-stone-mute">({form.target_ids.length} sélectionnée{form.target_ids.length > 1 ? "s" : ""})</span>}
                  </label>
                  <div data-testid="tf-categories" className="max-h-44 space-y-1 overflow-y-auto rounded-xl border border-amber-900/20 bg-cream p-2">
                    {categories.length === 0 ? (
                      <p className="px-2 py-1 text-xs text-stone-mute">Aucune catégorie disponible.</p>
                    ) : (
                      categories.map((c) => (
                        <label key={c.category_id} data-testid={`tf-category-${c.category_id}`} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-bark hover:bg-white">
                          <input type="checkbox" checked={form.target_ids.includes(c.category_id)} onChange={() => toggleCategory(c.category_id)} className="h-4 w-4 accent-amber-deep" />
                          <span className="truncate">{c.name}</span>
                        </label>
                      ))
                    )}
                  </div>
                  <p className="mt-1 text-xs text-stone-mute">Un palier identique sera créé pour chaque catégorie cochée.</p>
                </>
              )}
            </div>
            <div>
              <label htmlFor="tf-min-qty" className="mb-1.5 block text-sm font-medium text-bark">{form.scope === "global" ? "À partir de (articles au panier) *" : "À partir de (quantité) *"}</label>
              <input id="tf-min-qty" data-testid="tf-min-qty" type="number" min="2" max="100" required value={form.min_quantity} onChange={(e) => setForm({ ...form, min_quantity: e.target.value })} className={inputClass} />
            </div>
            <div>
              <label htmlFor="tf-discount" className="mb-1.5 block text-sm font-medium text-bark">Remise (%) *</label>
              <input id="tf-discount" data-testid="tf-discount" type="number" min="1" max="90" required value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: e.target.value })} className={inputClass} />
            </div>
          </div>
          <label data-testid="tf-active-label" className="flex cursor-pointer items-center gap-3 text-sm font-medium text-bark">
            <input data-testid="tf-active" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="h-4 w-4 accent-amber-deep" />
            Palier actif
          </label>
          <button
            data-testid="tf-submit"
            type="submit"
            disabled={saving}
            className="rounded-full bg-amber-deep px-6 py-3 text-sm font-semibold text-white shadow-md shadow-amber-900/15 transition-all duration-200 hover:bg-honey active:scale-95 disabled:opacity-60"
          >
            {saving ? "Enregistrement…" : editingId ? "Enregistrer" : "Créer le palier"}
          </button>
        </form>
      )}

      {!tiers ? (
        <p data-testid="pricing-loading" className="py-16 text-center font-serif text-xl text-bark-soft">Chargement…</p>
      ) : tiers.length === 0 ? (
        <p data-testid="pricing-empty" className="py-16 text-center font-serif text-xl text-bark-soft">Aucun palier — ex. « −10 % dès 3 miels ».</p>
      ) : (
        <div className="space-y-3">
          {tiers.map((t) => (
            <div key={t.tier_id} data-testid={`tier-row-${t.tier_id}`} className="flex flex-wrap items-center gap-4 rounded-2xl border border-amber-800/15 bg-white p-5 shadow-sm">
              <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${t.active ? "bg-amber-50 text-amber-deep" : "bg-sand text-stone-mute"}`}>
                <Percent size={22} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-serif text-lg font-semibold text-bark">
                  −{t.discount_percent} % dès {t.min_quantity} {t.scope === "product" ? "exemplaires" : "articles"}
                  {t.scope === "global" && <span className="ml-2 rounded-full bg-sage/15 px-2.5 py-0.5 text-xs font-medium text-sage">panier</span>}
                  {!t.active && <span className="ml-2 rounded-full bg-sand px-2.5 py-0.5 text-xs font-medium text-stone-mute">inactif</span>}
                </p>
                <p className="text-sm text-bark-soft">
                  {t.scope === "product" ? "Produit" : t.scope === "category" ? "Catégorie" : "Remise panier"} : {targetName(t)}
                </p>
              </div>
              <div className="flex shrink-0 gap-2">
                <button data-testid={`edit-tier-${t.tier_id}`} onClick={() => openEdit(t)} className="flex h-9 w-9 items-center justify-center rounded-full border border-amber-800/20 text-bark-soft transition-colors hover:border-amber-deep hover:text-amber-deep" aria-label="Modifier">
                  <Pencil size={15} />
                </button>
                <button data-testid={`delete-tier-${t.tier_id}`} onClick={() => handleDelete(t)} className="flex h-9 w-9 items-center justify-center rounded-full border border-terra/30 text-terra transition-colors hover:bg-terra/10" aria-label="Supprimer">
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
